import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useFieldArray } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

const httpMethodFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    method: z.string(),
    format: z.string(),
    timeout: z.number(),
    endpoint: z.string(),
    query: z.array(z.object({ type: z.string(), key: z.string(), value: z.string() })),
    header: z.array(z.object({ key: z.string(), value: z.string() })),
    body: z.array(z.object({ type: z.string(), key: z.string(), value: z.string() })),
});

export type HttpMethodFormData = z.infer<typeof httpMethodFormSchema>;

interface FormProps {
    onChange?: (data: HttpMethodFormData) => void;
    formId: string;
}

export const HttpMethodForm = ({ onChange, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    const defaultValues = {
        active: true,
        ...(selectedNode?.formData as HttpMethodFormData | undefined),
    } as HttpMethodFormData;
    return (
        <GeneralFormProvider<HttpMethodFormData>
            key={selectedNode?.id}
            id={formId}
            schema={httpMethodFormSchema}
            defaultValues={defaultValues}
        >
            <FormContent onChange={onChange} />
        </GeneralFormProvider>
    );
};

const FormContent = ({ onChange }: { onChange?: (data: HttpMethodFormData) => void }) => {
    const { control } = useGeneralForm<HttpMethodFormData>();
    const { t } = usePaldockTranslation("components.connection-creator");

    const { fields: queryFields, append: appendQuery, remove: removeQuery } = useFieldArray({ control, name: "query" });
    const {
        fields: headerFields,
        append: appendHeader,
        remove: removeHeader,
    } = useFieldArray({ control, name: "header" });
    const { fields: bodyFields, append: appendBody, remove: removeBody } = useFieldArray({ control, name: "body" });

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<HttpMethodFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.http_method.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("nodes.http_method.forms.title")}
                >
                    <Input placeholder={t("nodes.http_method.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.http_method.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.http_method.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Method, Format, Timeout row */}
            <Row gutter={16} className="mb-4">
                <Col span={8}>
                    <FormItem control={control} name="method" label={t("nodes.http_method.forms.method")}>
                        <Input placeholder={t("nodes.http_method.forms.method_placeholder")} />
                    </FormItem>
                </Col>
                <Col span={8}>
                    <FormItem control={control} name="format" label={t("nodes.http_method.forms.format")}>
                        <Input placeholder={t("nodes.http_method.forms.format_placeholder")} />
                    </FormItem>
                </Col>
                <Col span={8}>
                    <FormItem control={control} name="timeout" label={t("nodes.http_method.forms.timeout")}>
                        <Input type="number" placeholder={t("nodes.http_method.forms.timeout_placeholder")} />
                    </FormItem>
                </Col>
            </Row>

            {/* Endpoint */}
            <FormItem className="mb-4" control={control} name="endpoint" label={t("nodes.http_method.forms.endpoint")}>
                <TextArea rows={3} placeholder={t("nodes.http_method.forms.endpoint_placeholder")} />
            </FormItem>

            {/* Query section */}
            <DrawerCollapsible title={t("nodes.http_method.forms.query")} className="!bg-gray-100">
                <div className="flex gap-2 mb-2">
                    <Row className="flex-1" gutter={16}>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.type")}</Typography.Text>
                        </Col>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.key")}</Typography.Text>
                        </Col>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.value")}</Typography.Text>
                        </Col>
                    </Row>
                    <div className="w-[32px]" />
                </div>
                {queryFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                        <Row className="flex-1" gutter={16}>
                            <Col span={8}>
                                <FormItem control={control} name={`query.${index}.type`}>
                                    <Input />
                                </FormItem>
                            </Col>
                            <Col span={8}>
                                <FormItem control={control} name={`query.${index}.key`}>
                                    <Input />
                                </FormItem>
                            </Col>
                            <Col span={8}>
                                <FormItem control={control} name={`query.${index}.value`}>
                                    <Input />
                                </FormItem>
                            </Col>
                        </Row>
                        <Button
                            className="h-[32px]"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeQuery(index)}
                        />
                    </div>
                ))}
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    className="w-full"
                    onClick={() => appendQuery({ type: "", key: "", value: "" })}
                >
                    {t("nodes.http_method.forms.add_field")}
                </Button>
            </DrawerCollapsible>

            {/* Header section */}
            <DrawerCollapsible title={t("nodes.http_method.forms.header")} className="!bg-gray-100">
                <div className="flex gap-2 mb-2">
                    <Row className="flex-1" gutter={16}>
                        <Col span={12}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.key")}</Typography.Text>
                        </Col>
                        <Col span={12}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.value")}</Typography.Text>
                        </Col>
                    </Row>
                    <div className="w-[32px]" />
                </div>
                {headerFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                        <Row className="flex-1" gutter={16}>
                            <Col span={12}>
                                <FormItem control={control} name={`header.${index}.key`}>
                                    <Input />
                                </FormItem>
                            </Col>
                            <Col span={12}>
                                <FormItem control={control} name={`header.${index}.value`}>
                                    <Input />
                                </FormItem>
                            </Col>
                        </Row>
                        <Button
                            className="h-[32px]"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeHeader(index)}
                        />
                    </div>
                ))}
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    className="w-full"
                    onClick={() => appendHeader({ key: "", value: "" })}
                >
                    {t("nodes.http_method.forms.add_field")}
                </Button>
            </DrawerCollapsible>

            {/* Body section */}
            <DrawerCollapsible title={t("nodes.http_method.forms.body")} className="!bg-gray-100">
                <div className="flex gap-2 mb-2">
                    <Row className="flex-1" gutter={16}>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.type")}</Typography.Text>
                        </Col>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.key")}</Typography.Text>
                        </Col>
                        <Col span={8}>
                            <Typography.Text type="secondary">{t("nodes.http_method.forms.value")}</Typography.Text>
                        </Col>
                    </Row>
                    <div className="w-[32px]" />
                </div>
                {bodyFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                        <Row className="flex-1" gutter={16}>
                            <Col span={8}>
                                <FormItem control={control} name={`body.${index}.type`}>
                                    <Input />
                                </FormItem>
                            </Col>
                            <Col span={8}>
                                <FormItem control={control} name={`body.${index}.key`}>
                                    <Input />
                                </FormItem>
                            </Col>
                            <Col span={8}>
                                <FormItem control={control} name={`body.${index}.value`}>
                                    <Input />
                                </FormItem>
                            </Col>
                        </Row>
                        <Button
                            className="h-[32px]"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeBody(index)}
                        />
                    </div>
                ))}
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    className="w-full"
                    onClick={() => appendBody({ type: "", key: "", value: "" })}
                >
                    {t("nodes.http_method.forms.add_field")}
                </Button>
            </DrawerCollapsible>
        </div>
    );
};
